import type { ThemeConfig } from 'antd';
import { theme } from 'antd';

export const brand = {
  teal: '#1F5C5A',
  tealLight: '#4FA8A4',
  tealDeep: '#164340',
  copper: '#B5651D',
  copperLight: '#D4854A',
  walnutBg: '#1c130a',
  walnut: '#241A0E',
  walnut2: '#2e2010',
  walnutLine: '#3a2a1a',
  cream: '#FAF6F0',
  creamRaised: '#F0E8DC',
  creamLine: '#E0D4C4',
  ink: '#1A1208',
  inkMuted: '#6B5240',
  inkSubtle: '#b9a78a',
} as const;

const fontFamily = "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const fontFamilyCode = "'JetBrains Mono', ui-monospace, monospace";

export const afterHoursTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  cssVar: { key: 'sbd-menu' },
  hashed: true,
  token: {
    colorPrimary: brand.tealLight,
    colorInfo: brand.tealLight,
    colorWarning: brand.copper,
    colorError: '#E5484D',
    colorSuccess: brand.tealLight,
    colorBgBase: brand.walnutBg,
    colorBgContainer: brand.walnut,
    colorBgElevated: brand.walnut2,
    colorBgLayout: brand.walnutBg,
    colorBgSpotlight: brand.walnut2,
    colorTextBase: brand.cream,
    colorText: brand.cream,
    colorTextSecondary: brand.inkSubtle,
    colorTextTertiary: brand.inkMuted,
    // Secondary Typography reads this; it defaults to the tertiary ink, too dim on walnut to read.
    colorTextDescription: brand.inkSubtle,
    colorTextQuaternary: '#4a3624',
    colorBorder: brand.walnutLine,
    colorBorderSecondary: '#2a1d10',
    borderRadius: 5,
    borderRadiusLG: 8,
    borderRadiusSM: 3,
    borderRadiusXS: 2,
    fontFamily,
    fontFamilyCode,
    fontSize: 13,
    fontSizeHeading1: 28,
    fontSizeHeading2: 22,
    fontSizeHeading3: 18,
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,
    motionDurationMid: '180ms',
    motionDurationSlow: '240ms',
    sizeUnit: 4,
    sizeStep: 4,
  },
  components: {
    Layout: {
      headerBg: brand.walnut,
      headerHeight: 52,
      headerPadding: '0 24px',
      siderBg: brand.walnut,
      bodyBg: brand.walnutBg,
      footerBg: brand.walnut,
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: 'rgba(79, 168, 164, 0.12)',
      itemSelectedColor: brand.tealLight,
      itemHoverBg: brand.walnut2,
      itemHoverColor: brand.cream,
      itemColor: brand.inkSubtle,
      itemHeight: 36,
      iconSize: 14,
    },
    Button: {
      controlHeight: 32,
      controlHeightSM: 26,
      fontWeight: 600,
      primaryShadow: 'none',
      defaultShadow: 'none',
    },
    Table: {
      headerBg: brand.walnut,
      headerColor: brand.inkMuted,
      headerSplitColor: brand.walnutLine,
      rowHoverBg: brand.walnut2,
      borderColor: brand.walnutLine,
      cellPaddingBlock: 10,
      cellPaddingInline: 12,
      headerSortHoverBg: brand.walnut2,
      headerSortActiveBg: brand.walnut2,
    },
    Card: {
      headerBg: 'transparent',
      paddingLG: 20,
    },
    Statistic: {
      titleFontSize: 11,
      contentFontSize: 26,
    },
    Tag: {
      defaultBg: brand.walnut2,
      defaultColor: brand.inkSubtle,
    },
    Modal: {
      contentBg: brand.walnut,
      headerBg: brand.walnut,
    },
    Drawer: {
      colorBgElevated: brand.walnut,
    },
    Input: {
      activeBorderColor: brand.tealLight,
      hoverBorderColor: '#5a3f24',
    },
    // walnutLine is too faint against a modal for an unchecked radio button to show.
    Radio: {
      colorBorder: '#5a3f24',
    },
    Select: {
      optionSelectedBg: 'rgba(79, 168, 164, 0.12)',
      optionSelectedColor: brand.tealLight,
    },
  },
};

export const workshopTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  cssVar: { key: 'sbd-menu' },
  hashed: true,
  token: {
    colorPrimary: brand.teal,
    colorInfo: brand.teal,
    colorWarning: brand.copper,
    colorError: '#D9444A',
    colorSuccess: brand.teal,
    colorBgBase: brand.cream,
    colorBgContainer: '#FFFFFF',
    colorBgElevated: '#FFFFFF',
    colorBgLayout: brand.cream,
    colorBgSpotlight: brand.creamRaised,
    colorTextBase: brand.ink,
    colorText: brand.ink,
    colorTextSecondary: brand.inkMuted,
    colorTextTertiary: '#8a7560',
    colorTextQuaternary: brand.inkSubtle,
    colorBorder: brand.creamLine,
    colorBorderSecondary: '#EFE7DA',
    borderRadius: 5,
    borderRadiusLG: 8,
    borderRadiusSM: 3,
    borderRadiusXS: 2,
    fontFamily,
    fontFamilyCode,
    fontSize: 13,
    fontSizeHeading1: 28,
    fontSizeHeading2: 22,
    fontSizeHeading3: 18,
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,
    motionDurationMid: '180ms',
    motionDurationSlow: '240ms',
    sizeUnit: 4,
    sizeStep: 4,
  },
  components: {
    Layout: {
      headerBg: '#FFFFFF',
      headerHeight: 52,
      headerPadding: '0 24px',
      siderBg: '#FFFFFF',
      bodyBg: brand.cream,
      footerBg: '#FFFFFF',
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: 'rgba(31, 92, 90, 0.08)',
      itemSelectedColor: brand.teal,
      itemHoverBg: brand.creamRaised,
      itemHoverColor: brand.ink,
      itemColor: brand.inkMuted,
      itemHeight: 36,
      iconSize: 14,
    },
    Button: {
      controlHeight: 32,
      controlHeightSM: 26,
      fontWeight: 600,
      primaryShadow: 'none',
      defaultShadow: 'none',
    },
    Table: {
      headerBg: brand.creamRaised,
      headerColor: brand.inkMuted,
      headerSplitColor: brand.creamLine,
      rowHoverBg: brand.creamRaised,
      borderColor: brand.creamLine,
      cellPaddingBlock: 10,
      cellPaddingInline: 12,
    },
    Statistic: {
      titleFontSize: 11,
      contentFontSize: 26,
    },
    Tag: {
      defaultBg: brand.creamRaised,
      defaultColor: brand.inkMuted,
    },
  },
};
